"use client";

import { useGlobalQueryParams } from '../../hooks/queryParams/useGlobalQueryParams';
import { DynamicFormWithGallery } from "../../sections/DynamicFormWithGallery/DynamicFormWithGallery";
import { DynamicFormWithGalleryProps } from "../../sections/DynamicFormWithGallery/DynamicFormWithGallery.types";
import { interpolate, type DynamicStructure } from "../../utils/interpolate";
import { ImageProps } from "../../utils/image/types";
import Image from "next/image";

interface RelatedContentConfig {
  contentTypeId?: string;
  contentTypeRelationId?: string;
  contentTypeRelationIds?: string[];
  selectFieldName?: string;
  imageField?: string;
  selectLabel?: string;
  slot1TitleField?: string;
  slot1DescriptionField?: string;
  slot2TitleField?: string;
  slot2DescriptionField?: string;
  slot3TitleField?: string;
  slot3DescriptionField?: string;
  complementaryTextField?: string;
}

interface DynamicFormWithGalleryWrapperProps extends DynamicFormWithGalleryProps {
  dynamicStructure?: DynamicStructure;
  relatedContentConfig?: RelatedContentConfig;
  siteId?: string;
  accountId?: string;
  backgroundImageDesktopField?: string;
  backgroundImageMobileField?: string;
  backgroundVideoField?: string;
}

export default (compProps: DynamicFormWithGalleryWrapperProps) => {
  let resolvedImages = compProps.images;
  let resolvedImageFromDynamic: Record<string, any> | undefined;

  if (typeof compProps.images === "string" && compProps.dynamicStructure) {
    const result = interpolate({
      structure: compProps.dynamicStructure,
      template: compProps.images,
    });
    const gallery = result.gallery;
    if (gallery) {
      resolvedImages = gallery;
    } else {
      const image = result.image;
      if (image) {
        resolvedImageFromDynamic = image;
      }
      resolvedImages = [];
    }
  }

  const renderImage = (imageProps: ImageProps) => (
    <Image
      {...imageProps}
      width={imageProps.width || 686}
      height={imageProps.height || 471}
      loading={compProps.preload ? "eager" : "lazy"}
      priority={compProps.preload}
    />
  );

  const adaptGallery = (images: any[]) =>
    images.map((image: any) => ({
      render: renderImage,
      props: {
        src: image.src || image.props?.src,
        alt: image.alt || image.name || image.props?.alt || "",
        ...image.props,
      },
    }));

  const adaptSingleImage = (candidate: Record<string, any> | undefined) => {
    const src = candidate?.src || candidate?.props?.src;
    if (!src) return undefined;

    return {
      render: renderImage,
      props: {
        src,
        alt: candidate?.alt || candidate?.name || candidate?.props?.alt || "",
        ...candidate?.props,
      },
    };
  };

  const adaptedImages = adaptGallery(
    Array.isArray(resolvedImages) ? resolvedImages : [],
  );

  const adaptedImage = adaptSingleImage(
    resolvedImageFromDynamic ||
      (compProps.image as Record<string, any> | undefined),
  );

  /**
   * Imagem de cada opção do dropdown, resolvida contra o próprio item da relação e indexada pelo
   * mesmo valor da opção (`id || slug`). O bloco troca a imagem principal pela da opção escolhida.
   */
  const getRelatedMediaMap = () => {
    const imageField = compProps.relatedContentConfig?.imageField;
    const relationId = compProps.relatedContentConfig?.contentTypeRelationId;
    if (!imageField || !relationId || !compProps.dynamicStructure) return undefined;

    const items = compProps.dynamicStructure.item?.relations?.[relationId]?.items;
    if (!Array.isArray(items) || items.length === 0) return undefined;

    return items.reduce<NonNullable<DynamicFormWithGalleryProps["relatedMediaMap"]>>(
      (acc, item: Record<string, any>) => {
        const key = item.id || item.slug;
        if (!key) return acc;

        const result = interpolate({
          structure: { ...compProps.dynamicStructure!, item },
          template: imageField,
        });

        // Imagem antes da galeria: o `extractGallery` também aceita `|image` (galeria de um
        // item), e a opção com uma imagem só deve trocar a imagem, não virar carrossel.
        const image = adaptSingleImage(result.image);
        if (image) {
          acc[key] = { image };
        } else if (result.gallery?.length) {
          acc[key] = { images: adaptGallery(result.gallery) };
        }

        return acc;
      },
      {},
    );
  };

  const getBackgroundImageProps = () => {
    const isCmsImage = compProps.backgroundType === "cmsImage";
    const isStaticImage = compProps.backgroundType === "image";

    if (isCmsImage && compProps.dynamicStructure) {
      let desktopSrc: string | undefined;
      let mobileSrc: string | undefined;

      if (compProps.backgroundImageDesktopField) {
        const result = interpolate({
          structure: compProps.dynamicStructure,
          template: compProps.backgroundImageDesktopField,
        });
        const image = result.image;
        if (image && !Array.isArray(image)) {
          desktopSrc = image.src;
        } else {
          const text = result.text;
          if (typeof text === "string" && !text.includes("{{")) {
            desktopSrc = text;
          }
        }
      }

      if (compProps.backgroundImageMobileField) {
        const result = interpolate({
          structure: compProps.dynamicStructure,
          template: compProps.backgroundImageMobileField,
        });
        const image = result.image;
        if (image && !Array.isArray(image)) {
          mobileSrc = image.src;
        } else {
          const text = result.text;
          if (typeof text === "string" && !text.includes("{{")) {
            mobileSrc = text;
          }
        }
      }

      if (desktopSrc || mobileSrc) {
        return {
          desktop: {
            render: (props: ImageProps) => {
              return (
                <Image
                  {...props}
                  width={1920}
                  height={1080}
                  sizes="100vw"
                  loading={compProps.preload ? "eager" : "lazy"}
                  priority={compProps.preload}
                />
              );
            },
            props: { src: desktopSrc || mobileSrc || "", alt: "" },
          },
          mobile: {
            render: (props: ImageProps) => {
              return (
                <Image
                  {...props}
                  width={768}
                  height={1024}
                  sizes="100vw"
                  loading={compProps.preload ? "eager" : "lazy"}
                  priority={compProps.preload}
                />
              );
            },
            props: { src: mobileSrc || desktopSrc || "", alt: "" },
          },
        };
      }
    }

    if (!compProps.backgroundImage || !isStaticImage) {
      return undefined;
    }

    const bgImage = compProps.backgroundImage as any;

    if (bgImage.desktop?.props && bgImage.mobile?.props) {
      return {
        desktop: {
          render: (props: ImageProps) => {
            return (
              <Image
                {...props}
                width={1920}
                height={1080}
                sizes="100vw"
                loading={compProps.preload ? "eager" : "lazy"}
                priority={compProps.preload}
              />
            );
          },
          props: bgImage.desktop.props,
        },
        mobile: {
          render: (props: ImageProps) => {
            return (
              <Image
                {...props}
                width={768}
                height={1024}
                sizes="100vw"
                loading={compProps.preload ? "eager" : "lazy"}
                priority={compProps.preload}
              />
            );
          },
          props: bgImage.mobile.props,
        },
      };
    }

    return undefined;
  };

  const getVideoUrl = () => {
    if (
      compProps.backgroundType === "cmsVideo" &&
      compProps.backgroundVideoField &&
      compProps.dynamicStructure
    ) {
      const interpolated = interpolate({
        structure: compProps.dynamicStructure,
        template: compProps.backgroundVideoField,
      }).text;
      if (!interpolated.includes("{{")) {
        return interpolated;
      }
    }
    return compProps.urlVideo;
  };

  const resolvedBackgroundType =
    compProps.backgroundType === "cmsImage"
      ? "image"
      : compProps.backgroundType === "cmsVideo"
        ? "video"
        : compProps.backgroundType;

  const resolvedProduct = compProps.dynamicStructure?.item?.name
    ? String(compProps.dynamicStructure.item.name)
    : compProps.product;

  const resolvedChannel = compProps.dynamicStructure?.channel?.name
    ? String(compProps.dynamicStructure.channel.name)
    : undefined;

  const newProps = {
    ...compProps,
    images: adaptedImages,
    image: adaptedImage,
    relatedMediaMap: getRelatedMediaMap(),
    backgroundImage: getBackgroundImageProps(),
    backgroundType: resolvedBackgroundType,
    urlVideo: getVideoUrl(),
    highlightPreTitleColor: compProps.highlightPreTitleColor,
    highlightTitleColor: compProps.highlightTitleColor,
    highlightBgColor: compProps.highlightBgColor,
    product: resolvedProduct,
    channel: resolvedChannel,
    relatedContentConfig: {
      ...compProps.relatedContentConfig,
      contentTypeRelationIds:
        compProps.relatedContentConfig?.contentTypeRelationIds,
    },
  };

  return (
    <DynamicFormWithGallery
      {...newProps}
      dynamicStructure={compProps.dynamicStructure}
      useGlobalQueryParams={useGlobalQueryParams}
    />
  );
};
